import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, XCircle, Wrench } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

const PHASES = [
  "Subiendo archivo seguro…",
  "Analizando documento con Inteligencia Artificial…",
  "Estructurando datos y cruzando bases…",
  "Verificando cumplimiento normativo…",
  "Finalizando procesamiento…",
];

const PHASE_INTERVAL = 8_000; // 8 seconds per phase
const PROGRESS_DURATION = 45_000; // 45 seconds to reach 90%
const PROGRESS_TICK = 500;

type Status = "loading" | "success" | "error";

interface LoadingToastProps {
  status: Status;
  label?: string;
  fileName?: string;
  /** Solo presente cuando hubo un error real con job rescatable → habilita el Kill Switch. */
  onKillSwitch?: () => void;
}

export function LoadingToast({ status, label, fileName, onKillSwitch }: LoadingToastProps) {
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  // Phase cycling
  useEffect(() => {
    if (status !== "loading") return;
    const id = setInterval(() => {
      setPhaseIndex((prev) => (prev + 1) % PHASES.length);
    }, PHASE_INTERVAL);
    return () => clearInterval(id);
  }, [status]);

  // Simulated progress (ease-out curve to 90%)
  useEffect(() => {
    if (status !== "loading") {
      setProgress(100);
      return;
    }
    const id = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + PROGRESS_TICK;
        const ratio = Math.min(next / PROGRESS_DURATION, 1);
        // ease-out curve: fast start, slow end, caps at 90%
        const eased = 1 - Math.pow(1 - ratio, 2.5);
        setProgress(Math.round(eased * 90));
        return next;
      });
    }, PROGRESS_TICK);
    return () => clearInterval(id);
  }, [status]);

  const isLoading = status === "loading";
  const isSuccess = status === "success";

  return (
    <div className="flex flex-col gap-2 w-full min-w-[300px]">
      <div className="flex items-start gap-3">
        {isLoading && (
          <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0 mt-0.5" />
        )}
        {isSuccess && (
          <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        )}
        {status === "error" && (
          <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="text-sm font-medium text-foreground leading-tight">
            {label ?? (isLoading ? "Procesando documento" : isSuccess ? "Enviado con éxito" : "Error")}
          </p>
          <p className="text-xs text-muted-foreground leading-snug truncate">
            {isLoading ? PHASES[phaseIndex] : fileName ?? ""}
          </p>
        </div>
      </div>
      <Progress
        value={progress}
        className="h-1.5 bg-muted/50 [&>div]:transition-all [&>div]:duration-500 [&>div]:ease-out"
      />
      {status === "error" && onKillSwitch && (
        <Button
          size="sm"
          variant="outline"
          onClick={onKillSwitch}
          className="gap-2 self-start mt-1 border-amber-500/40 hover:bg-amber-500/10 text-amber-600 dark:text-amber-400"
        >
          <Wrench className="h-3.5 w-3.5" />
          Digitar Manualmente (Kill Switch)
        </Button>
      )}
    </div>
  );
}
